import type { SupportSubcategoryQuestion } from './types';

export const SHIRT_SIZE_KEYS = ['xs', 's', 'm', 'l', 'xl', '2xl', '3xl', '4xl', '5xl'] as const;
export type ShirtSizeKey = (typeof SHIRT_SIZE_KEYS)[number];

const DISPLAY_LABEL: Record<ShirtSizeKey, string> = {
  xs: 'XS',
  s: 'S',
  m: 'M',
  l: 'L',
  xl: 'XL',
  '2xl': '2XL',
  '3xl': '3XL',
  '4xl': '4XL',
  '5xl': '5XL',
};

export type ShirtSizeIdGroup = Record<ShirtSizeKey, string>;

export function shirtSizeQuestionId(prefix: string, segment: string, size: ShirtSizeKey): string {
  return `${prefix}-shirts-${segment}-${size}`;
}

export function buildShirtSizeIdGroup(prefix: string, segment: string): ShirtSizeIdGroup {
  return Object.fromEntries(
    SHIRT_SIZE_KEYS.map((size) => [size, shirtSizeQuestionId(prefix, segment, size)]),
  ) as ShirtSizeIdGroup;
}

export function makeShirtSizeQuestions(
  subcategoryId: string,
  labelPrefix: string,
  idGroup: ShirtSizeIdGroup,
  visibleWhen?: { questionId: string; value: string },
): SupportSubcategoryQuestion[] {
  return SHIRT_SIZE_KEYS.map((size) => ({
    id: idGroup[size],
    subcategoryId,
    type: 'text' as const,
    label: `${labelPrefix} ${DISPLAY_LABEL[size]}`,
    required: false,
    visibleWhen,
  }));
}

export type ShirtLineQuantityPayload = {
  xsQuantity: number;
  sQuantity: number;
  mQuantity: number;
  lQuantity: number;
  xlQuantity: number;
  '2xlQuantity': number;
  '3xlQuantity': number;
  '4xlQuantity': number;
  '5xlQuantity': number;
};

const PAYLOAD_QUANTITY_KEY: Record<ShirtSizeKey, keyof ShirtLineQuantityPayload> = {
  xs: 'xsQuantity',
  s: 'sQuantity',
  m: 'mQuantity',
  l: 'lQuantity',
  xl: 'xlQuantity',
  '2xl': '2xlQuantity',
  '3xl': '3xlQuantity',
  '4xl': '4xlQuantity',
  '5xl': '5xlQuantity',
};

export function parseAnswersToShirtQuantities(
  answers: Record<string, string>,
  idGroup: ShirtSizeIdGroup,
): ShirtLineQuantityPayload {
  const num = (v: string) => parseInt(String(v).replace(/[^0-9]/g, ''), 10) || 0;
  return Object.fromEntries(
    SHIRT_SIZE_KEYS.map((size) => [PAYLOAD_QUANTITY_KEY[size], num(answers[idGroup[size]])]),
  ) as ShirtLineQuantityPayload;
}
